import { useActiveAddress } from "@arweave-wallet-kit/react"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { AoInstance, useCachedAo, useAo } from "./ao"
import { useGlobalLiquidity } from "./monitor-api"
import { getSwapRoutesLocal, SwapRouteResult } from "./multihop-router"
import { TokenInfo, getBalance } from "./token"
import { useUserTokens } from "./user"
import { useTokenInfos } from "../hooks/use-token-info"
import { getDataSource } from "../services/data-source"
import { BLACKLIST, DEXI, REFRESH_INTERVAL_QUICK } from "../settings"
import { parseBigIntAsNumber } from "../utils/format"

/**
 * Get all tokens in the router process' pairs
 */
export async function getTokens(ao: AoInstance) {
  const dataSource = getDataSource(ao)
  return await dataSource.getTokensFromAMMFactory()
}

/**
 * Token ids hook
 */
export function useTokenIDs(): [string[], boolean] {
  // all token ids
  const [tokenIDs, setTokenIDs] = useState<string[]>([])

  // loading
  const [loading, setLoading] = useState(true)

  // ao instance
  const ao = useAo()

  useEffect(() => {
    ;(async () => {
      setLoading(true)

      try {
        const ids = await getTokens(ao)
        console.log("onFactoryTokens", ids)
        setTokenIDs(ids)
      } catch {}

      setLoading(false)
    })()
  }, [ao])

  return [tokenIDs, loading]
}

export interface RichTokenInfo extends TokenInfo {
  id: string
  balance?: bigint
  value?: number
  price?: number
  poolTvl?: number
  hasPool?: boolean
  approved?: boolean
}

/**
 * Hook for tokens with their info and balance (if connected)
 */
export function useTokens(): [RichTokenInfo[] | null] {
  // token ids
  const [ids, loadingIDs] = useTokenIDs()

  // arconnect tokens with balances
  const userTokens = useUserTokens()

  const allIds = useMemo(() => {
    if (!ids) return ids
    if (!userTokens) return ids

    const list = ids.slice()
    for (const userToken of userTokens) {
      const existing = list.find((t) => t === userToken.id)

      if (!existing) list.push(userToken.id)
    }

    return list.filter((x) => !BLACKLIST.includes(x))
  }, [ids, userTokens])

  // tokens
  const [tokens, loadingTokenInfos] = useTokenInfos(allIds)

  // fetch balances
  const [balances, setBalances] = useState<Record<string, bigint>>({})

  // fetch prices
  const [loadingPrices, setLoadingPrices] = useState(true)
  const [prices, setPrices] = useState<Record<string, { price: number }>>({})

  const [loadingBalances, setLoadingBalances] = useState<boolean>(false)

  const { data: approvedTokens, isLoading: loadingApprovedTokens } = useQuery({
    queryKey: ["get-approved-tokens"],
    queryFn: async () => {
      if (!ao) return [] // No ao instance available

      const dataSource = getDataSource(ao)
      try {
        return await dataSource.getApprovedTokens()
      } catch (error) {
        console.error("Error fetching approved tokens from data source", error)
      }
    },
  })

  // tokens with balances & prices
  const richTokens = useMemo(() => {
    if (!tokens) return null

    const list = tokens.map((token) => {
      const balance = balances[token.id]
      const price = prices[token.id]?.price
      const hasPool = token.id in prices
      const value =
        (price || 0) * Number(parseBigIntAsNumber(balance, token.denomination, token.denomination))
      const approved = approvedTokens?.includes(token.id)

      return {
        ...token,
        balance,
        price,
        value,
        hasPool,
        approved,
      }
    })

    // sort by value, otherwise sort by balance
    list.sort((a, b) => {
      if (a.value && !b.value) return -1
      if (!a.value && b.value) return 1
      if (a.value && b.value) return a.value > b.value ? -1 : 1

      if (a.balance && !b.balance) return -1
      if (!a.balance && b.balance) return 1
      if (a.balance && b.balance)
        return a.balance * 10n ** BigInt(b.denomination) > b.balance * 10n ** BigInt(a.denomination)
          ? -1
          : 1

      if (a.price && !b.price) return -1
      if (!a.price && b.price) return 1
      if (a.price && b.price) return a.price > b.price ? -1 : 1

      return 0
    })

    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens, balances, prices, approvedTokens])

  // ao instance
  const aoCached = useCachedAo()
  const ao = useAo()

  // active address
  const activeAddr = useActiveAddress()

  // manage loading state
  const loading = useMemo(
    () => !!loadingIDs || !!loadingTokenInfos || !!loadingPrices,
    [loadingIDs, loadingTokenInfos, loadingPrices],
  )

  // fetch balances
  useEffect(() => {
    ;(async () => {
      if (allIds.length === 0 || loadingIDs || !activeAddr) {
        setLoadingBalances(false)
        setBalances({})
        return
      }
      setLoadingBalances(true)

      try {
        await Promise.all([
          allIds.forEach(async (id) => {
            try {
              const balance = await getBalance(id, activeAddr, ao)
              setBalances((prev) => ({
                ...prev,
                [id]: balance,
              }))
            } catch (error) {
              console.error(`Error fetching balance for token ${id}:`, error)
              // Keep the previous balance if it exists, otherwise use 0n
              setBalances((prev) => ({
                ...prev,
                [id]: prev[id] || 0n,
              }))
            }
          }),
        ])
        setLoadingBalances(false)
      } catch (error) {
        console.error("Failed to fetch balances", error)
        setLoadingBalances(false)
      }
    })()
  }, [allIds, loadingIDs, activeAddr, ao])

  // fetch prices for all tokens (user tokens included)
  useEffect(() => {
    ;(async () => {
      if (richTokens === null || richTokens.length === 0 || loadingIDs) {
        setLoadingPrices(false)
        return setPrices({})
      }
      setLoadingPrices(true)

      try {
        const res = await aoCached.dryrun({
          process: DEXI,
          tags: [
            { name: "Action", value: "Get-Price-For-Tokens" },
            { name: "Tokens", value: JSON.stringify(richTokens.map((x) => x.id)) },
          ],
        })
        const prices = res.Messages[0].Tags.find((x: any) => x.name === "Prices")?.value
        console.log("onTokenPrices", prices)
        setPrices(prices)
        setLoadingPrices(false)
      } catch (error) {
        console.error("Failed to fetch prices", error)
        setLoadingPrices(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIds, userTokens, loadingIDs, activeAddr, ao])

  return [loading ? null : richTokens]
}

/**
 * Token pair type
 */
export type Pair = [string, string]

export async function getPoolByTokens(pair: Pair, ao: AoInstance, feePercentageBps?: bigint) {
  const dataSource = getDataSource(ao)
  const poolResult = await dataSource.getPoolByTokens(pair, feePercentageBps)

  if (poolResult.pending) {
    throw new Error("Pool is pending, please try again later")
  }

  return poolResult
}

export function useSuggestedPool(tokenA?: string, tokenB?: string, feePercentageBps?: bigint) {
  // ao instance
  const ao = useAo()

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["get-pool", tokenA, tokenB, ao, feePercentageBps ? feePercentageBps.toString() : ""],
    queryFn: async () => {
      if (!tokenA || !tokenB) return

      return await getPoolByTokens([tokenA, tokenB], ao, feePercentageBps)
    },
    retry: false,
    enabled: !!tokenA && !!tokenB,
  })

  useEffect(() => {
    if (!data || data.pending === false) return

    const interval = setInterval(() => {
      refetch()
    }, REFRESH_INTERVAL_QUICK)

    return () => clearInterval(interval)
  }, [data, refetch])

  return [data, isLoading, refetch, isRefetching] as const
}

export async function getSwapRoutes(pair: Pair, ao: AoInstance, amount: string) {
  const tags = [
    {
      name: "Action",
      value: "Router/Get-Swap-Routes",
    },
    {
      name: "From-Token",
      value: pair[0],
    },
    {
      name: "To-Token",
      value: pair[1],
    },
    {
      name: "Amount-In",
      value: amount,
    },
  ]

  const result = await ao.dryrun({
    process: DEXI,
    tags,
  })

  console.log("onSwapRoutesResult", result)

  try {
    if (result.Messages.length === 0) throw new Error(result.Output.data)

    const data = JSON.parse(result.Messages[0].Data) as SwapRouteResult

    return data
  } catch (error) {
    console.error(error)
    throw new Error("Failed to get swap route")
  }
}

/**
 * Calculate shortest path
 */
export function calculateShortestPath(data: SwapRouteResult) {
  // sort from all_routes the shortest pool_addresses.length
  const routes = data.all_routes.sort((a, b) => {
    return a.pool_addresses.length - b.pool_addresses.length
  })

  // take only the shortest routes (might be more with same length)
  const shortestRoutes = routes.filter(
    (r) => r.pool_addresses.length === routes[0].pool_addresses.length,
  )

  // Take only the one with the biggest amount_out
  const bestRoute = shortestRoutes.sort((a, b) => {
    const diff = BigInt(b.amount_out) - BigInt(a.amount_out)
    if (diff === 0n) return 0
    return diff > 0n ? 1 : -1
  })[0]

  return {
    dijkstra_route: {
      amount_out: bestRoute.amount_out,
      path: bestRoute.path,
      pool_addresses: bestRoute.pool_addresses,
      distance: bestRoute.pool_addresses.length,
      amounts: bestRoute.amounts,
    },
    all_routes: routes,
  }
}

export function useSwapRoute(tokenA?: string, tokenB?: string, amount?: string) {
  const { data: pools } = useGlobalLiquidity()

  // ao instance
  // const ao = useAo()

  // const {
  //    data, isLoading, refetch, isRefetching
  // } = useQuery({
  //   queryKey: ["Router/Get-Swap-Routes", tokenA, tokenB, amount],
  //   queryFn: async () => {
  //     if (!tokenA || !tokenB || !amount) return

  //     const swapRoute = await getSwapRoutes([tokenA, tokenB], ao, amount)
  //     console.log("onSwapRoute", swapRoute)
  //     return swapRoute
  //   },
  //   retry: false,
  //   enabled: !!tokenA && !!tokenB,
  // })

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["Local/Get-Swap-Routes", tokenA, tokenB, amount],
    queryFn: () => {
      if (!tokenA || !tokenB || !amount || !pools) return

      try {
        const swapRoute = getSwapRoutesLocal(pools, tokenA, tokenB, amount)
        console.log("onSwapRouteLocal", swapRoute)
        return swapRoute
      } catch (error) {
        console.error("onSwapRouteLocalError", error)
        return null
      }
    },
    retry: false,
    enabled: !!tokenA && !!tokenB && !!pools,
  })

  useEffect(() => {
    if (!data) return

    const interval = setTimeout(() => {
      refetch()
    }, REFRESH_INTERVAL_QUICK)

    return () => clearInterval(interval)
  }, [data, refetch])

  if (data && data.all_routes.length > 0) {
    return {
      data: calculateShortestPath(data),
      isLoading,
      refetch,
      isRefetching,
    }
  }

  return { data, isLoading, refetch, isRefetching }
}

type Pool = [string, string]
export type PoolMap = Record<string, Pool>
export type PoolOverview = {
  quote_token_process: string
  quote_token_ticker: string
  quote_token_name: string
  base_token_process: string
  base_token_ticker: string
  base_token_name: string
  tvl_rank: number
  tvl_in_usd: number
  tvl_in_quote: number
  tx_count: number
  apr_30d: number
  amm_process: string
  pool_fee_bps: number
  liquidity_usd: number
}

export async function getPoolMap(ao: AoInstance): Promise<PoolMap> {
  const dataSource = getDataSource(ao)

  try {
    return await dataSource.getPoolMap()
  } catch (error) {
    console.error("Error fetching pool map from data source, falling back to dryrun", error)

    return {}
  }
}
